import api, {
  makeAuthenticatedRequest,
  makePublicRequest,
} from "../../../app/axios/apiClient";
import { ContactDTO, Plan } from "../../common/types";

export type CustomerRecord = {
  id: number;
  region_id: number;
  first_name: string;
  last_name: string;
  job_title: string;
  work_email_address: string;
  phone_code: string;
  mobile_phone: string;
  organization_name: string;
  organization_domain: string;
  external_cust_ref: string;
  organization_size: string;
  status: number;
  account_type: string;
  requested_licenses: number;
  createdAt?: string;
  updated_at?: string;
  schema?: { schema_name: string };
};

export type FetchCustomersParams = {
  mspId: number;
  page?: number;
  limit?: number;
  search?: string;
};

export type CustomerApiResponse = {
  status: string;
  message: string;
  data: CustomerRecord[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRows: number;
  };
};

export type AddCustomerRequest = {
  company_name: string;
  website: string;
  tax_id?: string;
  timezone: string;
  region_id: number;
  plan_price_id: number;
  no_of_licenses: number;
  total_paid_amount: number;
  discount?: number;
  payment_mode: string;
  transaction_ref?: string;
  date_of_payment: string;
  expiration_date: string;
  comment?: string;
  msp_id: number;
  contacts: {
    first_name: string;
    last_name: string | null;
    corporate_email: string;
    designation: string | null;
    position: string | null;
    mobile_phone: string;
    work_phone_no?: string;
    contact_type: "company" | "primary";
    addresses: {
      street_address?: string;
      country: number;
      state: number;
      city: number;
      postal_code: string;
      is_default: boolean;
    }[];
  }[];
};

export type AddCustomerResponse = {
  status: string;
  message: string;
  data: CustomerRecord;
};

export type LicensePayload = {
  licenses: number;
};

export const fetchCustomers = async (
  params: FetchCustomersParams
): Promise<CustomerApiResponse> => {
  const { mspId, page = 1, limit = 10, search = "" } = params;
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps/${mspId}/customers`,
      params: { page, limit, search },
      skipLoadingOverlay: true,
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch customers:", error);
    throw new Error(
      error.response?.data?.message || "Failed to fetch customers"
    );
  }
};

export const addCustomer = async (
  customerData: AddCustomerRequest
): Promise<AddCustomerResponse> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/customers`,
      data: customerData,
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add customer:", error);
    throw new Error(error.response?.data?.message || "Failed to add customer");
  }
};

export const fetchCustomerInformation = async (
  mspId: number,
  customerId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/msps/${mspId}/customers/${customerId}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to get customer info:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to get customer info. Please try again."
    );
  }
};

export const updateCustomer = async (
  customerId: number,
  data: any
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/customers/${customerId}`,
      data,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to update customer:", error);
    throw new Error(
      error.response?.data?.message || "Failed to update customer"
    );
  }
};

export const addCustomerProduct = async (data: any): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/customers/add-products`,
      data,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add Customer product:", error);
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add Customer product. Please try again."
    );
  }
};

export const getCustomerContact = async (
  customerId: number,
  customerContactId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "GET",
      url: `/customers/${customerId}/contacts/${customerContactId}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to get Customer contact info:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to get Customer contact info. Please try again."
    );
  }
};

export const updateCustomerContact = async (
  customerId: number,
  customerContactId: number,
  apiPayload: ContactDTO
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/customers/${customerId}/contacts/${customerContactId}`,
      data: apiPayload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to update customer contact info:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update Customer contact info. Please try again."
    );
  }
};

export const addCustomerContact = async (
  customerId: number,
  apiPayload: ContactDTO
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "POST",
      url: `/customers/${customerId}/contacts`,
      data: apiPayload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add customer contact:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add customer contact. Please try again."
    );
  }
};

export const fetchProducts = async (): Promise<[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/products`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load products:", error);
    throw new Error(error.response?.data?.message || "Failed to load products");
  }
};

export const fetchCustomerPlans = async (
  productId: number
): Promise<Plan[]> => {
  try {
    const response = await makePublicRequest({
      method: "GET",
      url: `/msps/customers/products/${productId}/plans`,
      headers: { accept: "*/*" },
      skipLoadingOverlay: true,
    });
    return response.data.data;
  } catch (error: any) {
    console.error("Failed to load plans:", error);
    throw new Error(error.response?.data?.message || "Failed to load plans");
  }
};

export const addLicensesToCustomerSubscription = async (
  mspId: number,
  customerId: number,
  // subscriptionId: number,
  productId: number,
  payload: LicensePayload
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${mspId}/customer/${customerId}/add-license/${productId}`,
      // url: `/msps/${mspId}/customer/${customerId}/subscription/${subscriptionId}/add-license`,
      data: payload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Failed to add licenses to subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to add licenses. Please try again."
    );
  }
};

export const reduceLicensesFromCustomerSubscription = async (
  mspId: number,
  customerId: number,
  // subscriptionId: number,
  productId: number,
  payload: LicensePayload
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      // url: `/msps/${mspId}/customer/${customerId}/subscription/${productId}/reduce-license`,
      url: `/msps/${mspId}/customer/${customerId}/reduce-license/${productId}`,
      data: payload,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to reduce licenses from subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to reduce licenses. Please try again."
    );
  }
};

export const cancelCustomerSubscription = async (
  mspId: number,
  customerId: number,
  subscriptionId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "PUT",
      url: `/msps/${mspId}/customer/${customerId}/subscription/${subscriptionId}/cancel`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to cancel customer subscription:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to cancel customer subscription. Please try again."
    );
  }
};

export const deleteCustomerContact = async (
  customerId: number,
  contactId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "DELETE",
      url: `/customers/${customerId}/contacts/${contactId}`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to delete customer contact:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error(
        "Authentication required. Please check your login status."
      );
    }

    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete customer contact. Please try again."
    );
  }
};

export const deactivateCustomer = async (
  mspId: number,
  customerId: number
): Promise<any> => {
  try {
    const response = await makeAuthenticatedRequest({
      method: "DELETE",
      url: `/msps/${mspId}/customer/${customerId}/deactivate`,
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      suppressSuccessToast: true,
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to deactivate customer:", error);
    throw new Error(
      error.response?.data?.message || "Failed to deactivate customer"
    );
  }
};

export default api;
